import { pickActiveEvmWallet, connectEvmWallet } from './detectEvmWallets';
import { EVM_WALLET_SIGN_MSG, evmWalletMismatchMsg } from './evmWalletMessages';
import { signHlUserTypedData, formatHlSignNetworkError } from './hlSignNetwork';
import { parseApiError } from './hlDepositMessages';

/**
 * Move USDC between Hyperliquid Spot and Perps (browser wallet signature).
 * Default: Spot → Perps so Single Asset live trades can use the balance.
 * Server verifies balances actually changed before returning success.
 */
function formatMoveError(e, address) {
  const msg = String(e?.response?.data?.error || e.message || '');
  if (/unified account is active/i.test(msg)) {
    return (
      'Unified account is active on Hyperliquid — spot and perps already share one balance. ' +
      'Skip Move and trade directly.'
    );
  }
  return parseApiError(e, address);
}

export async function moveUsdcSpotToPerpsWithBrowserWallet({
  walletAddress,
  amountUsd,
  apiBase,
  authHeaders,
  toPerp = true,
}) {
  const w = await pickActiveEvmWallet();
  if (!w) {
    throw new Error(
      `${EVM_WALLET_SIGN_MSG} Use the same 0x address linked in Omni (MetaMask, Rabby, Phantom Ethereum, etc.).`
    );
  }

  let address;
  try {
    ({ address } = await connectEvmWallet(w.provider));
  } catch (e) {
    throw new Error(formatHlSignNetworkError(e));
  }

  if (walletAddress && address.toLowerCase() !== String(walletAddress).trim().toLowerCase()) {
    throw new Error(evmWalletMismatchMsg(address));
  }

  const axios = (await import('axios')).default;
  let prepared;
  try {
    const { data } = await axios.post(
      `${apiBase}/hyperliquid/usd-class-transfer/prepare`,
      { walletAddress: address, amountUsd, toPerp },
      { headers: authHeaders() }
    );
    prepared = data;
  } catch (e) {
    throw new Error(formatMoveError(e, address));
  }

  let signature;
  try {
    signature = await signHlUserTypedData(w.provider, address, prepared.typedData);
  } catch (e) {
    throw new Error(formatHlSignNetworkError(e));
  }

  try {
    const { data: submitted } = await axios.post(
      `${apiBase}/hyperliquid/usd-class-transfer/submit`,
      {
        action: prepared.action,
        nonce: prepared.nonce,
        signature,
        walletAddress: address,
        amountUsd: prepared.amountUsd,
        toPerp: prepared.toPerp,
      },
      { headers: authHeaders() }
    );

    if (!submitted?.success) {
      throw new Error(submitted?.error || 'Transfer failed');
    }

    return {
      ...submitted,
      amountUsd: prepared.amountUsd,
      toPerp: prepared.toPerp,
      balances: submitted.balances || null,
      verified: !!submitted.verified,
    };
  } catch (e) {
    throw new Error(formatMoveError(e, address));
  }
}
